import { useState } from 'react';
import { ArrowLeft, ChevronRight, ChevronLeft } from 'lucide-react';
import { bibleData } from '../data/bible';

interface BibleReaderProps {
  onBack: () => void;
}

export function BibleReader({ onBack }: BibleReaderProps) {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const chapter = bibleData[currentChapterIndex];

  const handleNext = () => {
    if (currentChapterIndex < bibleData.length - 1) {
      setCurrentChapterIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentChapterIndex > 0) {
      setCurrentChapterIndex(prev => prev - 1);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 font-serif text-stone-900 flex flex-col pb-24">
      {/* Header */}
      <div className="bg-white px-6 py-4 pt-safe-top border-b border-stone-200 sticky top-0 z-10 shadow-sm flex items-center justify-between">
        <button 
          onClick={onBack}
          className="p-2 -ml-2 rounded-full hover:bg-stone-100 text-stone-600 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex flex-col items-center">
          <span className="text-xs uppercase tracking-widest text-stone-500 font-sans">The Bible</span>
          <h2 className="text-xl font-bold flex items-center gap-2">
            {chapter.book} {chapter.chapter}
          </h2>
        </div>
        <div className="w-10"></div> {/* Spacer */}
      </div>

      {/* Reader Content */}
      <div className="flex-1 max-w-2xl mx-auto w-full p-6 pb-24">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-stone-200 mb-8 relative">
           <div className="absolute top-0 left-0 w-full h-1 bg-stone-200"></div>
           
           <div className="mb-6 pb-6 border-b border-stone-100">
             <p className="text-stone-500 italic text-sm font-sans leading-relaxed">
               "{chapter.description}"
             </p>
           </div>

           <div className="space-y-6">
             {chapter.verses.map((verse, idx) => (
               <p key={idx} className="leading-loose text-lg text-stone-800">
                 <span className="text-stone-400 text-xs font-sans mr-2 align-top mt-1 inline-block select-none">{idx + 1}</span>
                 {verse}
               </p>
             ))}
           </div>
        </div>

        {/* Navigation */}
      <div className="flex justify-between items-center px-4 max-w-2xl mx-auto w-full pb-8 safe-area-bottom">
        <button 
          onClick={handlePrev}
          disabled={currentChapterIndex === 0}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors ${
            currentChapterIndex === 0 
              ? 'text-stone-300 cursor-not-allowed' 
              : 'text-stone-600 hover:bg-stone-200 hover:text-stone-900'
          }`}
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>

        <span className="text-xs text-stone-400 font-sans">
          {currentChapterIndex + 1} / {bibleData.length}
        </span>

        <button 
          onClick={handleNext}
          disabled={currentChapterIndex === bibleData.length - 1}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors ${
            currentChapterIndex === bibleData.length - 1
              ? 'text-stone-300 cursor-not-allowed' 
              : 'text-stone-600 hover:bg-stone-200 hover:text-stone-900'
          }`}
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      </div>
    </div>
  );
}
